import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import { changePassword } from 'services/auth.service';

export default function UserProfileModal({ isOpen, onClose }) {
  const { user, displayName } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetPasswordForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleClose = () => {
    resetPasswordForm();
    onClose();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!user?.userId) {
      toast({ title: 'No se pudo identificar al usuario', status: 'error', duration: 3000 });
      return;
    }

    if (newPassword.length < 8) {
      toast({ title: 'La nueva contraseña debe tener al menos 8 caracteres', status: 'warning', duration: 3000 });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({ title: 'Las contraseñas no coinciden', status: 'warning', duration: 3000 });
      return;
    }

    try {
      setSaving(true);
      const response = await changePassword({
        userId: user.userId,
        oldPassword,
        newPassword,
      });

      if (response?.status === 200) {
        toast({
          title: 'Contraseña actualizada',
          description: 'Su contraseña fue cambiada correctamente',
          status: 'success',
          duration: 4000,
        });
        resetPasswordForm();
      }
    } catch (error) {
      toast({
        title: 'Error al cambiar contraseña',
        description: error?.response?.data?.message || error?.message || 'Intente nuevamente',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Mi perfil</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <VStack align="stretch" spacing={1}>
              <Text fontSize="lg" fontWeight="700" color={textColor}>
                {displayName}
              </Text>
              <Text fontSize="sm" color={subTextColor}>
                Usuario: {user?.username || '-'}
              </Text>
              {user?.person?.document && (
                <Text fontSize="sm" color={subTextColor}>
                  Documento: {user.person.document}
                </Text>
              )}
              {user?.roles?.length > 0 && (
                <HStack spacing={2} pt={1} flexWrap="wrap">
                  {user.roles.map((role) => (
                    <Badge key={role} colorScheme="blue">
                      {role}
                    </Badge>
                  ))}
                </HStack>
              )}
            </VStack>

            <Divider />

            <Text fontWeight="600" color={textColor}>
              Cambiar contraseña
            </Text>

            <form onSubmit={handleChangePassword}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Contraseña actual</FormLabel>
                  <InputGroup>
                    <Input
                      type={showOld ? 'text' : 'password'}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                    />
                    <InputRightElement h="full">
                      <Icon
                        as={showOld ? MdVisibilityOff : MdVisibility}
                        cursor="pointer"
                        onClick={() => setShowOld(!showOld)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Nueva contraseña</FormLabel>
                  <InputGroup>
                    <Input
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <InputRightElement h="full">
                      <Icon
                        as={showNew ? MdVisibilityOff : MdVisibility}
                        cursor="pointer"
                        onClick={() => setShowNew(!showNew)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Confirmar nueva contraseña</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <InputRightElement h="full">
                      <Icon
                        as={showConfirm ? MdVisibilityOff : MdVisibility}
                        cursor="pointer"
                        onClick={() => setShowConfirm(!showConfirm)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="brand"
                  isDisabled={!oldPassword || !newPassword || !confirmPassword}
                >
                  Actualizar contraseña
                </Button>
              </VStack>
            </form>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
